import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, Eye } from 'lucide-react';
import { useAuth } from '../context/AppContext';
import { articleService, categoryService, authorService } from '../services/api';
import styles from './ArticleEditorPage.module.css';
import TiptapEditor from '../components/editor/TiptapEditor';
import ImageUpload from '../components/common/ImageUpload';
import ArticleCommentsPanel from '../components/ArticleCommentsPanel';

export default function ArticleEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, hasNewsRole } = useAuth();
  
  // const isNew = id === 'nuevo';
  const isNew = !id;
  
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    categoryId: '',
    authorId: '',
    image: '',
    imageCaption: '',
    tags: '',
    featured: false,
    trending: false,
    status: 'published',
    // Campos preservados: no se editan desde el UI actual pero se
    // cargan del artículo y se reenvían al guardar para que el
    // backend no los sobrescriba con vacíos por default.
    slug: '',
    subtitle: '',
  });
  
  const [categories, setCategories] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Guarda el estado original al cargar. Al guardar, si el estado
  // del formulario difiere de éste, disparamos el endpoint dedicado
  // /estado (PATCH), que es el único que persiste 'estado' y
  // 'publicado_en'. El PUT no toca esos campos por diseño.
  const initialStatusRef = useRef('draft');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    loadData();
  }, [id, isAuthenticated, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [categoriesData, authorsData] = await Promise.all([
        categoryService.getAll(),
        authorService.getAll()
      ]);
      
      setCategories(categoriesData || []);
      setAuthors(authorsData || []);
      
      if (!isNew) {
        // const article = await articleService.getById(parseInt(id));
        const article = await articleService.getById(id);
        setFormData({
          title: article.title || '',
          excerpt: article.excerpt || '',
          content: article.content || '',
          categoryId: article.categoryId || '',
          authorId: article.authorId || '',
          image: article.image || '',
          imageCaption: article.imageCaption ?? article.imageAlt ?? '',
          tags: article.tags?.join(', ') || '',
          featured: article.featured ?? article.isFeatured ?? false,
          trending: article.isBreaking || false,
          status: article.status || 'draft',
          slug: article.slug || '',
          subtitle: article.subtitle || '',
        });
        initialStatusRef.current = article.status || 'draft';
      }
    } catch (err) {
      console.error('Error detallado:', err);
      setError('Error al cargar datos: ' + err.message);
      // setError('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.categoryId || !formData.authorId) {
      setError('Categoría y autor son requeridos');
      return;
    }
    setSaving(true);

    try {
      // const data = {
      //   ...formData,
      //   categoryId: parseInt(formData.categoryId),
      //   authorId: parseInt(formData.authorId),
      //   tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean)
      // };

      const data = {
        title: formData.title,
        subtitle: formData.subtitle,
        slug: formData.slug,
        excerpt: formData.excerpt,
        content: formData.content,
        image: formData.image,
        imageCaption: formData.imageCaption,
        categoryId: formData.categoryId,
        authorId: formData.authorId,
        featured: formData.featured,
        breaking: formData.trending,
        status: formData.status,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean)
      };

      let response;
if (isNew) {
  response = await articleService.create(data);
  // Si el usuario eligió un estado distinto de borrador al crear,
  // aplicarlo con changeStatus tras el create (el POST siempre deja
  // el artículo en 'borrador' por diseño del backend).
  if (response?.id && formData.status && formData.status !== 'draft') {
    await articleService.changeStatus(response.id, formData.status);
  }
} else {
  response = await articleService.update(id, data);
  // Si el estado cambió respecto al inicial, disparamos el endpoint
  // dedicado /estado (PATCH). Es el único que persiste el estado y
  // fija publicado_en cuando pasa a 'published'. El PUT no toca esos
  // campos por diseño del backend.
  if (formData.status && formData.status !== initialStatusRef.current) {
    await articleService.changeStatus(id, formData.status);
    initialStatusRef.current = formData.status;
  }
}

alert(response.message || 'Artículo guardado correctamente');
navigate('/admin');
    } catch (err) {
      setError('Error al guardar el artículo');
    } finally {
      setSaving(false);
    }
  };

  if (!isAuthenticated) return null;

  if (loading) {
    return <div className={styles.loading}>Cargando...</div>;
  }

  return (
    <main className={styles.editorPage}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <Link to="/admin" className={styles.backBtn}>
            <ArrowLeft size={20} />
            Volver
          </Link>
          <h1>{isNew ? 'Nuevo Artículo' : 'Editar Artículo'}</h1>
        </div>
        <div className={styles.headerActions}>
          {/* <button type="button" className={styles.previewBtn}>
            <Eye size={18} />
            Vista previa
          </button> */}
          <button 
            type="button" 
            className={styles.previewBtn}
            onClick={() => {
              if (formData.title) {
                const previewWindow = window.open('', '_blank');
                previewWindow.document.write(`
                  <!DOCTYPE html>
                  <html>
                  <head>
                    <title>${formData.title}</title>
                    <style>
                      body { font-family: Georgia, serif; max-width: 800px; margin: 40px auto; padding: 20px; line-height: 1.7; }
                      h1 { font-size: 2.5em; margin-bottom: 0.5em; }
                      .excerpt { font-size: 1.2em; color: #666; border-left: 4px solid #d2691e; padding-left: 16px; }
                      img { max-width: 100%; height: auto; margin: 20px 0; }
                      .caption { font-size: 0.9em; color: #888; font-style: italic; }
                    </style>
                  </head>
                  <body>
                    <h1>${formData.title}</h1>
                    ${formData.excerpt ? `<p class="excerpt">${formData.excerpt}</p>` : ''}
                    ${formData.image ? `<img src="${formData.image}" alt="${formData.title}"><p class="caption">${formData.imageCaption || ''}</p>` : ''}
                    <div>${formData.content}</div>
                  </body>
                  </html>
                `);
                previewWindow.document.close();
              } else {
                alert('Escribe un título primero');
              }
            }}
          >
            <Eye size={18} />
            Vista previa
          </button>
          <button 
            type="submit" 
            form="articleForm"
            className={styles.saveBtn}
            disabled={saving}
          >
            <Save size={18} />
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </header>

      {error && <div className={styles.error}>{error}</div>}

      <form id="articleForm" onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.mainColumn}>
          <div className={styles.field}>
            <label>Título</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Título del artículo"
              required
            />
          </div>

          <div className={styles.field}>
            <label>Extracto</label>
            <textarea
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              placeholder="Breve descripción del artículo..."
              rows={3}
            />
          </div>

          {/* <div className={styles.field}>
            <label>Contenido</label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="Escribe el contenido del artículo aquí... (soporta HTML)"
              rows={20}
              required
            />
          </div> */}

          <div className={styles.field}>
            <label>Contenido</label>
            <TiptapEditor
              content={formData.content}
              onChange={(value) => setFormData({ ...formData, content: value })}
              placeholder="Escribe el contenido del artículo..."
              articleId={id}
              onNotify={(msg) => alert(msg)}
            />
          </div>

          {/* Comentarios del equipo — solo si el artículo ya existe */}
          {!isNew && id && <ArticleCommentsPanel articuloId={id} />}
        </div>

        <div className={styles.sideColumn}>
          <div className={styles.panel}>
            <h3>Publicación</h3>
            
            <div className={styles.field}>
              <label>Categoría</label>
              <select
                value={formData.categoryId}
                onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                required
              >
                <option value="">Seleccionar...</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div className={styles.field}>
              <label>Autor</label>
              <select
                value={formData.authorId}
                onChange={(e) => setFormData({ ...formData, authorId: e.target.value })}
                required
              >
                <option value="">Seleccionar...</option>
                {authors.map(author => (
                  <option key={author.id} value={author.id}>{author.name}</option>
                ))}
              </select>
            </div>
            
            <div className={styles.field}>
              <label>Estado</label>
              {(() => {
                // Colaborador solo puede guardar en borrador. Redactor
                // puede publicar directamente si el periódico no exige
                // aprobación (si la exige, el backend responde 403 al
                // intentar); en cualquier caso también puede En revisión.
                // Editor puede todos los estados.
                const esEditor    = hasNewsRole('Editor');
                const esRedactor  = hasNewsRole('Redactor');
                return (
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="draft">Borrador</option>
                    {(esEditor || esRedactor) && (
                      <option value="in_review">En revisión</option>
                    )}
                    {(esEditor || esRedactor) && (
                      <option value="published">Publicado</option>
                    )}
                    {esEditor && <option value="archived">Archivado</option>}
                  </select>
                );
              })()}
            </div>

            {/* Destacado / Trending solo tienen sentido para artículos
                que llegan a publicarse. Colaborador ni siquiera puede
                publicar, así que se ocultan. */}
            {(hasNewsRole('Editor') || hasNewsRole('Redactor')) && (
            <div className={styles.checkboxes}>
              <label>
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                />
                Artículo destacado
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={formData.trending}
                  onChange={(e) => setFormData({ ...formData, trending: e.target.checked })}
                />
                Trending
              </label>
            </div>
            )}
          </div>

          <div className={styles.panel}>
            <h3>Imagen principal</h3>
            
            {/* <div className={styles.field}>
              <label>URL de la imagen</label>
              <input
                type="url"
                value={formData.image}
                onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                placeholder="https://..."
              />
            </div>

            {formData.image && (
              <img src={formData.image} alt="Preview" className={styles.imagePreview} />
            )} */}

            <div className={styles.field}>
              <label>Imagen principal</label>
              <ImageUpload
                value={formData.image}
                onChange={(url) => setFormData({ ...formData, image: url })}
                folder="articles"
                placeholder="https://... o sube una imagen"
              />
            </div>

            <div className={styles.field}>
              <label>Pie de foto</label>
              <input
                type="text"
                value={formData.imageCaption}
                onChange={(e) => setFormData({ ...formData, imageCaption: e.target.value })}
                placeholder="Descripción de la imagen"
              />
            </div>
          </div>

          <div className={styles.panel}>
            <h3>Etiquetas</h3>
            <div className={styles.field}>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="tecnología, innovación, startups"
              />
              <small>Separadas por comas</small>
            </div>
          </div>
        </div>
      </form>
    </main>
  );
}
